/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  DollarSign,
  TrendingUp,
  Wallet,
  Search,
  Check,
  X,
  Loader2,
  UserCheck,
  UserX,
  SlidersHorizontal,
  ArrowUpRight,
  ShieldCheck,
  Settings2,
  CreditCard,
  Sparkles,
} from "lucide-react";
import { authHeaders } from "../../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

const tabs = [
  { key: "dashboard", label: "Overview", icon: Sparkles },
  { key: "users", label: "Referrers", icon: Users },
  { key: "payouts", label: "Payout Requests", icon: CreditCard },
  { key: "settings", label: "Settings", icon: Settings2 },
];

export default function AdminReferrals() {
  const [tab, setTab] = useState("dashboard");

  return (
    <div className="min-h-screen bg-[#F6F8FB] p-5 lg:p-8" data-testid="admin-referrals">
      {/* Header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#111827] via-[#1E293B] to-[#0F172A] px-6 py-7 lg:px-8 lg:py-8 text-white mb-7">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-emerald-400 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-blue-500 blur-3xl" />
        </div>

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold mb-4">
              <ShieldCheck size={13} />
              Referral Management
            </div>

            <h1 className="text-3xl lg:text-4xl font-black tracking-tight">
              Partner & Referral Program
            </h1>

            <p className="text-sm text-white/70 mt-2 max-w-2xl">
              Manage affiliate partners, referral earnings, payout approvals and
              global commission configurations from one dashboard.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="bg-white/10 backdrop-blur border border-white/10 rounded-2xl px-4 py-3 min-w-[150px]">
              <div className="text-white/60 text-[11px] uppercase font-bold">
                Program Status
              </div>
              <div className="mt-1 flex items-center gap-2 text-sm font-bold">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                Active
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto mb-7 pb-1">
        {tabs.map((t) => {
          const Icon = t.icon;

          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`adminref-tab-${t.key}`}
              className={`group flex items-center gap-2 px-4 py-3 rounded-2xl whitespace-nowrap transition-all text-sm font-bold border
              ${
                tab === t.key
                  ? "bg-white border-transparent shadow-sm text-[#111827]"
                  : "bg-transparent border-transparent text-black/50 hover:text-black hover:bg-white/70"
              }`}
            >
              <Icon
                size={16}
                className={
                  tab === t.key
                    ? "text-emerald-600"
                    : "text-black/40 group-hover:text-black/70"
                }
              />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && <Dashboard />}
      {tab === "users" && <ReferrersList />}
      {tab === "payouts" && <PayoutsList />}
      {tab === "settings" && <ReferralSettings />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Dashboard                                */
/* -------------------------------------------------------------------------- */

function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/referral/stats`, {
        headers: authHeaders(),
      })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  if (!stats) return <Spinner />;

  const totalEarnings =
    (stats.earnings?.pending || 0) + (stats.earnings?.paid || 0);

  return (
    <div data-testid="adminref-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <KPI
          title="Total Referrers"
          value={stats.users}
          icon={Users}
          growth="+12%"
          accent="emerald"
        />

        <KPI
          title="Tenant Referrals"
          value={stats.relationships}
          icon={TrendingUp}
          growth="+8%"
          accent="blue"
        />

        <KPI
          title="Pending Payouts"
          value={stats.pending_payouts}
          icon={Wallet}
          growth="Needs review"
          accent="amber"
        />

        <KPI
          title="Total Earnings"
          value={ngn(totalEarnings)}
          icon={DollarSign}
          growth="+18%"
          accent="violet"
        />
      </div>

      {/* Content */}
      <div className="grid xl:grid-cols-[1.2fr,0.8fr] gap-5 mt-6">
        {/* Earnings Breakdown */}
        <div className="bg-white rounded-3xl border border-black/5 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-black text-black">
                Earnings Breakdown
              </h3>
              <p className="text-sm text-black/50 mt-1">
                Overview of paid and pending commissions
              </p>
            </div>

            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <DollarSign size={20} />
            </div>
          </div>

          <div className="space-y-4">
            {Object.entries(stats.earnings || {}).map(([k, v]) => (
              <div
                key={k}
                className="flex items-center justify-between p-4 rounded-2xl border border-black/5 bg-[#FAFBFC]"
              >
                <div>
                  <div className="text-xs uppercase font-bold text-black/45">
                    {k}
                  </div>
                  <div className="font-bold text-lg mt-1">{ngn(v)}</div>
                </div>

                <div className="w-11 h-11 rounded-xl bg-white border border-black/5 flex items-center justify-center">
                  <ArrowUpRight size={18} className="text-emerald-600" />
                </div>
              </div>
            ))}

            {Object.keys(stats.earnings || {}).length === 0 && (
              <Empty msg="No earnings yet" icon={DollarSign} />
            )}
          </div>
        </div>

        {/* Quick Summary */}
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl text-white p-6 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs font-bold">
              <Sparkles size={12} />
              Program Insights
            </div>

            <h3 className="mt-5 text-2xl font-black leading-tight">
              Referral program is performing well.
            </h3>

            <p className="mt-3 text-sm text-white/80 leading-relaxed">
              Continue monitoring payout approvals and optimize referral
              commission settings to improve partner acquisition.
            </p>

            <div className="mt-8 space-y-4">
              <div className="bg-white/10 rounded-2xl p-4 backdrop-blur">
                <div className="text-xs uppercase font-bold text-white/60">
                  Active Referrers
                </div>
                <div className="mt-1 text-3xl font-black">{stats.users}</div>
              </div>

              <div className="bg-white/10 rounded-2xl p-4 backdrop-blur">
                <div className="text-xs uppercase font-bold text-white/60">
                  Pending Reviews
                </div>
                <div className="mt-1 text-3xl font-black">
                  {stats.pending_payouts}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Referrers List                               */
/* -------------------------------------------------------------------------- */

function ReferrersList() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setUsers(
        (
          await axios.get(`${API}/admin/referral/users`, {
            headers: authHeaders(),
          })
        ).data || []
      );
    } catch {
      //
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (u) => {
    setBusy(true);

    try {
      await axios.patch(
        `${API}/admin/referral/users/${u.id}`,
        { is_active: !u.is_active },
        { headers: authHeaders() }
      );

      toast.success(u.is_active ? "User suspended" : "User activated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const adjust = async (u) => {
    const v = window.prompt(
      `Adjust wallet for ${u.full_name}\nCurrent: ${ngn(
        u.wallet_balance
      )}\n\nEnter new wallet balance:`,
      String(u.wallet_balance || 0)
    );

    if (v === null) return;

    const num = parseFloat(v);

    if (isNaN(num)) return toast.error("Invalid amount");

    try {
      await axios.patch(
        `${API}/admin/referral/users/${u.id}`,
        { wallet_balance: num },
        { headers: authHeaders() }
      );

      toast.success("Wallet updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const filtered = useMemo(() => {
    return users.filter(
      (u) =>
        !q ||
        (u.full_name || "").toLowerCase().includes(q.toLowerCase()) ||
        (u.email || "").toLowerCase().includes(q.toLowerCase()) ||
        (u.referral_code || "").toLowerCase().includes(q.toLowerCase())
    );
  }, [q, users]);

  return (
    <div data-testid="adminref-users">
      {/* Topbar */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
        <div className="relative w-full max-w-md">
          <Search
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-black/35"
          />

          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search referrers..."
            data-testid="adminref-users-search"
            className="w-full pl-11 pr-4 py-3 rounded-2xl border border-black/10 bg-white shadow-sm text-sm focus:outline-none focus:border-emerald-500"
          />
        </div>

        <div className="text-sm text-black/55 font-medium">
          Showing{" "}
          <span className="font-bold text-black">{filtered.length}</span> of{" "}
          <span className="font-bold text-black">{users.length}</span>{" "}
          referrers
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl border border-black/5 shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <Empty msg="No referrers found" icon={Users} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-[#F8FAFC] border-b border-black/5">
                <tr className="text-[11px] uppercase tracking-wider text-black/45">
                  <TH>Name</TH>
                  <TH>Email</TH>
                  <TH>Code</TH>
                  <TH right>Wallet</TH>
                  <TH right>Lifetime</TH>
                  <TH right>Referrals</TH>
                  <TH>Status</TH>
                  <TH>Actions</TH>
                </tr>
              </thead>

              <tbody>
                {filtered.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-black/5 hover:bg-[#FAFBFC] transition-colors"
                  >
                    <TD>
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-black">
                          {(u.full_name || "U").charAt(0)}
                        </div>

                        <div>
                          <div className="font-bold text-black">
                            {u.full_name}
                          </div>
                          <div className="text-xs text-black/45">
                            ID #{u.id}
                          </div>
                        </div>
                      </div>
                    </TD>

                    <TD className="text-black/65">{u.email}</TD>

                    <TD>
                      <span className="inline-flex items-center rounded-xl bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-bold">
                        {u.referral_code}
                      </span>
                    </TD>

                    <TD right className="font-bold text-emerald-700">
                      {ngn(u.wallet_balance)}
                    </TD>

                    <TD right>{ngn(u.lifetime_earnings)}</TD>

                    <TD right>{u.referrals_count || 0}</TD>

                    <TD>
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold ${
                          u.is_active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        {u.is_active ? "Active" : "Suspended"}
                      </span>
                    </TD>

                    <TD>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => adjust(u)}
                          className="px-3 py-2 rounded-xl bg-blue-50 text-blue-700 text-xs font-bold hover:bg-blue-100"
                        >
                          Wallet
                        </button>

                        <button
                          onClick={() => toggle(u)}
                          disabled={busy}
                          className={`px-3 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1
                          ${
                            u.is_active
                              ? "bg-red-50 text-red-600 hover:bg-red-100"
                              : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          }`}
                        >
                          {u.is_active ? (
                            <>
                              <UserX size={12} />
                              Suspend
                            </>
                          ) : (
                            <>
                              <UserCheck size={12} />
                              Activate
                            </>
                          )}
                        </button>
                      </div>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Payouts List                                */
/* -------------------------------------------------------------------------- */

function PayoutsList() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");

  const load = async () => {
    try {
      setItems(
        (
          await axios.get(
            `${API}/admin/referral/payouts?status=${status}`,
            {
              headers: authHeaders(),
            }
          )
        ).data || []
      );
    } catch {
      //
    }
  };

  useEffect(() => {
    load();
  }, [status]);

  const approve = async (id) => {
    try {
      await axios.post(
        `${API}/admin/referral/payouts/${id}/approve`,
        {},
        { headers: authHeaders() }
      );

      toast.success("Payout approved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const reject = async (id) => {
    const reason = window.prompt("Rejection reason?");

    if (reason === null) return;

    try {
      await axios.post(
        `${API}/admin/referral/payouts/${id}/reject`,
        { reason },
        { headers: authHeaders() }
      );

      toast.success("Payout rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="adminref-payouts">
      {/* Filters */}
      <div className="flex items-center gap-2 mb-5">
        {["pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-4 py-2 rounded-2xl text-xs font-bold capitalize transition-all
            ${
              status === s
                ? "bg-[#111827] text-white shadow-sm"
                : "bg-white border border-black/10 text-black/55 hover:text-black"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl border border-black/5 shadow-sm overflow-hidden">
        {items.length === 0 ? (
          <Empty msg={`No ${status} payouts`} icon={Wallet} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead className="bg-[#F8FAFC] border-b border-black/5">
                <tr className="text-[11px] uppercase tracking-wider text-black/45">
                  <TH>Referrer</TH>
                  <TH>Bank</TH>
                  <TH>Account</TH>
                  <TH right>Amount</TH>
                  <TH>Requested</TH>
                  <TH>Actions</TH>
                </tr>
              </thead>

              <tbody>
                {items.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-black/5 hover:bg-[#FAFBFC]"
                  >
                    <TD className="font-bold">
                      {p.referrer_name || p.referral_user_id}
                    </TD>

                    <TD>{p.bank_name}</TD>

                    <TD>
                      <div className="font-mono text-sm">
                        {p.account_number}
                      </div>
                      <div className="text-xs text-black/45">
                        {p.account_name}
                      </div>
                    </TD>

                    <TD right className="font-black text-emerald-700">
                      {ngn(p.amount)}
                    </TD>

                    <TD className="text-sm text-black/55">
                      {p.created_at?.slice(0, 10)}
                    </TD>

                    <TD>
                      {p.status === "pending" ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => approve(p.id)}
                            className="px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-bold inline-flex items-center gap-1 hover:bg-emerald-100"
                          >
                            <Check size={12} />
                            Approve
                          </button>

                          <button
                            onClick={() => reject(p.id)}
                            className="px-3 py-2 rounded-xl bg-red-50 text-red-600 text-xs font-bold inline-flex items-center gap-1 hover:bg-red-100"
                          >
                            <X size={12} />
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-[11px] font-bold ${
                            p.status === "approved"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {p.status}
                        </span>
                      )}
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                  Settings                                  */
/* -------------------------------------------------------------------------- */

function ReferralSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/referral/settings`, {
        headers: authHeaders(),
      })
      .then((r) => setS(r.data || {}))
      .catch(() =>
        setS({
          default_pct: 10,
          min_payout_amount: 5000,
          enabled: true,
        })
      );
  }, []);

  if (!s) return <Spinner />;

  const save = async () => {
    setSaving(true);

    try {
      await axios.patch(`${API}/admin/referral/settings`, s, {
        headers: authHeaders(),
      });

      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white border border-black/5 shadow-sm rounded-3xl p-6 max-w-3xl"
      data-testid="adminref-settings"
    >
      <div className="flex items-center gap-3 mb-7">
        <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center">
          <SlidersHorizontal size={20} />
        </div>

        <div>
          <h3 className="text-xl font-black text-black">
            Referral Configuration
          </h3>
          <p className="text-sm text-black/50">
            Configure default commissions and payout policies
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field lbl="Default Referral Commission %">
          <input
            type="number"
            step="0.5"
            value={s.default_pct || 0}
            onChange={(e) =>
              setS({
                ...s,
                default_pct: parseFloat(e.target.value) || 0,
              })
            }
            className="input"
          />
        </Field>

        <Field lbl="Minimum Payout Amount (₦)">
          <input
            type="number"
            value={s.min_payout_amount || 0}
            onChange={(e) =>
              setS({
                ...s,
                min_payout_amount: parseFloat(e.target.value) || 0,
              })
            }
            className="input"
          />
        </Field>
      </div>

      <div className="mt-5">
        <Field lbl="Landing Page Tagline">
          <input
            value={s.landing_tagline || ""}
            onChange={(e) =>
              setS({
                ...s,
                landing_tagline: e.target.value,
              })
            }
            className="input"
          />
        </Field>
      </div>

      <div className="mt-6 p-4 rounded-2xl bg-[#F8FAFC] border border-black/5 flex items-center justify-between">
        <div>
          <div className="font-bold text-black">Referral Program Status</div>
          <div className="text-sm text-black/50 mt-1">
            Disable new referral registrations and activities
          </div>
        </div>

        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={!!s.enabled}
            onChange={(e) =>
              setS({
                ...s,
                enabled: e.target.checked,
              })
            }
            className="sr-only peer"
          />

          <div className="w-14 h-8 bg-black/10 rounded-full peer peer-checked:bg-emerald-600 transition-all" />

          <div className="absolute left-1 top-1 w-6 h-6 rounded-full bg-white transition-all peer-checked:translate-x-6" />
        </label>
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="mt-6 w-full md:w-auto px-6 py-3 rounded-2xl bg-[#111827] text-white font-bold text-sm hover:opacity-95 disabled:opacity-50"
      >
        {saving ? "Saving changes..." : "Save Settings"}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                  Components                                */
/* -------------------------------------------------------------------------- */

function KPI({ title, value, icon: Icon, accent, growth }) {
  const styles = {
    emerald: "bg-emerald-50 text-emerald-700",
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
  };

  return (
    <div className="bg-white rounded-3xl border border-black/5 shadow-sm p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase font-bold tracking-wide text-black/45">
            {title}
          </div>

          <div className="mt-2 text-3xl font-black tracking-tight text-black">
            {value}
          </div>

          {growth && (
            <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
              <ArrowUpRight size={12} />
              {growth}
            </div>
          )}
        </div>

        <div
          className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
            styles[accent] || "bg-black/5 text-black/60"
          }`}
        >
          <Icon size={22} />
        </div>
      </div>
    </div>
  );
}

function TH({ children, right }) {
  return (
    <th
      className={`px-5 py-4 font-bold ${
        right ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function TD({ children, right, className = "" }) {
  return (
    <td
      className={`px-5 py-4 text-sm ${
        right ? "text-right" : ""
      } ${className}`}
    >
      {children}
    </td>
  );
}

function Field({ lbl, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase font-bold tracking-wider text-black/45 mb-2">
        {lbl}
      </label>

      {children}
    </div>
  );
}

function Empty({ msg, icon: Icon }) {
  return (
    <div className="py-20 text-center">
      <div className="w-16 h-16 rounded-3xl bg-black/5 flex items-center justify-center mx-auto mb-4">
        <Icon size={28} className="text-black/30" />
      </div>

      <div className="font-bold text-black/55">{msg}</div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="p-16 flex items-center justify-center text-black/45">
      <Loader2 className="animate-spin" />
    </div>
  );
}